import { styled } from '@mui/material/styles';
import { Select, MenuItem, FormHelperText } from '@mui/material';
import { SxProps, Theme } from '@mui/material';

interface StyledUnitSelectProps {
  height?: string;
  width?: string;
  hasError?: boolean;
  fontSize?: string;
  fontWeight?: string;
  borderColor?: string;
}

export const StyledUnitSelect = styled(Select, {
  shouldForwardProp: (prop) =>
    prop !== 'height' &&
    prop !== 'width' &&
    prop !== 'hasError' &&
    prop !== 'fontSize' &&
    prop !== 'fontWeight' &&
    prop !== 'borderColor',
})<StyledUnitSelectProps>(
  ({
    height = '40px',
    width = '450px',
    hasError = false,
    fontSize = '16px',
    fontWeight = '400',
    borderColor = 'var(--border)',
  }) => ({
    height: height,
    width: width,
    backgroundColor: 'var(--text-bg)',
    border: 'none',
    borderRadius: '4px',
    color: 'var(--primary-navy)',

    '& .MuiSelect-select': {
      padding: '10px 14px',
      fontFamily: 'Outfit',
      fontSize: fontSize,
      fontWeight: fontWeight,
    },

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: hasError ? 'var(--error)' : borderColor,
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--primary-blue)',
    },

    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--primary-blue)',
    },

    '& input': {
      fontFamily: 'Outfit',
      fontSize: '16px',
    },

    overflow: 'hidden',

    '& input:-webkit-autofill': {
      backgroundColor: 'var(--bg-color)',
      WebkitBoxShadow: '0 0 0 30px var(--bg-color) inset',
    },

    '& input:-moz-autofill': {
      backgroundColor: 'var(--bg-color)',
      boxShadow: '0 0 0 30px var(--bg-color) inset',
    },

    '& input:-ms-autofill': {
      backgroundColor: 'var(--bg-color)',
      boxShadow: '0 0 0 30px var(--bg-color) inset',
    },
  })
);

export const StyledUnitMenuItem = styled(MenuItem)({
  fontFamily: 'Outfit',
  fontSize: '16px',

  '&.Mui-selected': {
    backgroundColor: 'var(--inactive-btn)',
    color: 'var(--primary-blue)',
  },
});

export const unitSelectMenuProps = {
  PaperProps: {
    style: {
      maxHeight: '235px',
      backgroundColor: 'var(--bg-color)',
      overflowY: 'auto' as const,
    },
    sx: {
      mt: 0.5,
      borderRadius: 2,
      border: '1px solid var(--border)',
      bgcolor: 'var(--text-bg)',
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
      '&::-webkit-scrollbar': {
        width: '6px',
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'var(--bg-color)',
        borderRadius: '4px',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'var(--loader-color)',
        borderRadius: '4px',
        height: '70px',
      },
      '&::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'var(--primary-blue)',
        cursor: 'pointer',
      },
    },
  },
};

export const unitChevronIconStyles: SxProps<Theme> = {
  position: 'absolute',
  top: '50%',
  right: '12px',
  transform: 'translateY(-50%)',
  fontSize: '16px',
  color: 'var(--instructions)',
  '&:hover': { cursor: 'pointer' },
};

export const unitLabelStyles: SxProps<Theme> = {
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '16px',
  color: 'var(--primary-navy)',
  mb: 2,
};

export const unitErrorIconStyles: SxProps<Theme> = {
  fontSize: '16px',
  color: 'var(--error)',
  mr: 0.8,
};

export const unitErrorTextStyles: SxProps<Theme> = {
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '14px',
  color: 'var(--error)',
  display: 'flex',
  alignItems: 'center',
  position: 'absolute',
  top: '34px',
  ml: 0,
  mt: 1,
};

interface StyledDatePickerErrorTextProps {
  top?: string;
}

export const StyledDatePickerErrorText = styled(FormHelperText, {
  shouldForwardProp: (prop) => prop !== 'top',
})<StyledDatePickerErrorTextProps>(({ top = '4px' }) => ({
  fontFamily: 'Outfit',
  fontWeight: '400',
  fontSize: '14px',
  color: 'var(--error)',
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  top: top,
  marginLeft: 0,
  marginTop: '-24px',
}));
